/* eslint-disable no-await-in-loop */
/**
 * 自建任务：按解析结果串行创建关联任务与资产任务。
 */
import { bulkSetFormData, formatImageFiles } from '@gUtils';
import { projectsAPI } from '@api';
import { resolveAssetTypeCodeForBatch } from '@/utils/batch_self_built_asset_folder';

/**
 * 从创建任务接口响应中提取资产ID。
 * @param {Object} taskData 创建任务返回数据。
 * @returns {string|number} 资产ID，缺失时返回空字符串。
 */
function extractCreatedAssetId(taskData) {
    if (!taskData || typeof taskData !== 'object') {
        return '';
    }
    // 资产导出优先使用资产内部ID（如 MDCNTDRWXH01），避免误用任务ID。
    return taskData.asset_uid
        || taskData.assetUid
        || taskData.asset_id
        || taskData.assetId
        || taskData?.asset?.id
        || taskData?.asset?.asset_uid
        || taskData?.asset?.assetUid
        || taskData?.asset?.asset_id
        || '';
}

/**
 * 创建一条自建批量资产。
 * @param {Object} ctx 上下文。
 * @param {Object} item 单条解析结果。
 * @returns {Promise<Object>} 成功或失败信息。
 */
async function createOneSelfBuiltAsset(ctx, item) {
    const { formResult, projectId, taskStore } = ctx;
    const { folderName, previewFile, hasFbx } = item;
    const assetTypeCode = resolveAssetTypeCodeForBatch(hasFbx, taskStore.asset_type);
    if (!assetTypeCode) {
        return { ok: false, err: '无法匹配资产类型（3D模型/贴图），请检查任务配置' };
    }
    let fd = new FormData();
    fd.append('package_id', formResult.package_id);
    fd.append('material_type', formResult.material_type);
    fd.append('title', folderName);
    fd = formatImageFiles([{ originFileObj: previewFile }], fd);
    fd.append('type', 2);
    const creatTask = await projectsAPI.associatedTask(fd);
    if (creatTask.code !== 1000) {
        return { ok: false, err: creatTask.msg || '创建关联任务失败' };
    }
    const packageItemId = creatTask.data.id;
    const createParams = {
        asset_name: folderName,
        asset_type: assetTypeCode,
        location: formResult.location,
        photographers: formResult.photographers,
        collect_images_count: formResult.collect_images_count,
        asset_comment: formResult.asset_comment,
        asset_character: formResult.asset_character ?? 0
    };
    let fd2 = new FormData();
    fd2.append('project_id', projectId);
    fd2.append('type', 0);
    fd2.append('package_item_id', packageItemId);
    if (taskStore.assetsId) {
        fd2.append('asset_id', taskStore.assetsId);
    }
    fd2 = bulkSetFormData(createParams, fd2);
    const taskResult = await taskStore.createTask(fd2);
    if (taskResult.code !== 1000) {
        return { ok: false, err: taskResult.msg || '创建资产失败' };
    }
    return {
        ok: true,
        assetId: extractCreatedAssetId(taskResult.data),
        assetName: folderName
    };
}

/**
 * 串行执行批量创建，收集失败列表。
 * @param {Object} options 入参对象。
 * @returns {Promise<Object>} ok、failed、total 统计。
 */
export async function runBatchSelfBuiltAssetCreate(options) {
    const { items, formResult, projectId, taskStore } = options;
    const failed = [];
    const succeeded = [];
    let ok = 0;
    const ctx = { formResult, projectId, taskStore };
    for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        try {
            const res = await createOneSelfBuiltAsset(ctx, item);
            if (res.ok) {
                ok += 1;
                succeeded.push({
                    assetId: res.assetId || item.folderName,
                    assetName: res.assetName || item.folderName
                });
            } else {
                failed.push({ folderName: item.folderName, err: res.err || '未知错误' });
            }
        } catch (err) {
            failed.push({
                folderName: item.folderName,
                err: err.message || String(err)
            });
        }
    }
    return { ok, failed, succeeded, total: items.length };
}
